// Frontend Email Service - Connects to Flask Backend API
// This service sends requests to the Python/Flask backend for email processing

const API_CONFIG = {
  // Backend API base URL - when served by Flask, use relative URLs
  baseUrl: process.env.REACT_APP_API_BASE_URL || '',
  endpoints: {
    submitRequest: '/api/solution-requests/submit',
    testEmail: '/api/solution-requests/test-email',
    health: '/api/solution-requests/health'
  }
};

// Main function to submit solution request via backend API
export const sendSolutionRequestEmail = async (formData) => {
  try {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.submitRequest}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return {
      success: true,
      messageId: result.request_id || `req_${Date.now()}`,
      status: 'sent',
      service: 'Flask Backend API',
      details: result
    };

  } catch (error) {
    console.error('❌ Error submitting solution request:', error);
    
    // Provide user-friendly error messages
    let errorMessage = 'Failed to send request. Please try again or contact IT support.';
    
    if (error.message.includes('fetch')) {
      errorMessage = 'Unable to connect to server. Please check your connection and try again.';
    } else if (error.message.includes('400')) {
      errorMessage = 'Invalid request data. Please check all required fields.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Server error occurred. Please try again later or contact IT support.';
    }
    
    throw new Error(errorMessage);
  }
};

// Test the email service connection
export const testEmailService = async () => {
  try {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.testEmail}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contactName: 'Frontend Test',
        contactEmail: 'frontend-test@knipper.com',
        title: 'Frontend Connection Test',
        description: 'Testing connection between React frontend and Flask backend.'
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Test failed: ${response.status}`);
    }

    return result;

  } catch (error) {
    console.error('Email service test failed:', error);
    throw error;
  }
};

// Check backend service health
export const checkServiceHealth = async () => {
  try {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.health}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Health check failed: ${response.status}`);
    }

    return result;

  } catch (error) {
    console.error('Service health check failed:', error);
    throw error;
  }
};

// Validate form data before submission
export const validateFormData = (formData) => {
  const errors = [];
  
  // Required fields validation
  const requiredFields = {
    department: 'Department',
    title: 'Solution Title',
    description: 'Description',
    contactName: 'Contact Name',
    contactEmail: 'Contact Email'
  };
  
  Object.entries(requiredFields).forEach(([field, label]) => {
    if (!formData[field] || formData[field].trim() === '') {
      errors.push(`${label} is required`);
    }
  });
  
  // Email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (formData.contactEmail && !emailRegex.test(formData.contactEmail)) {
    errors.push('Please enter a valid email address');
  }
  
  // Custom department validation
  if (formData.department === 'other' && (!formData.customDepartment || formData.customDepartment.trim() === '')) {
    errors.push('Please specify your department when "Other" is selected');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  sendSolutionRequestEmail,
  testEmailService,
  checkServiceHealth,
  validateFormData
};

